import KpiChart from "@app/components/kpi-chart";
import { useToast } from "@app/components/toast";
import { environment } from "@app/relay/relay-environment";
import type { dividendsPage_Query } from "@app/queries/dividendsPage_Query.graphql";
import type { dividendsPage_ShareholderMutation } from "@app/queries/dividendsPage_ShareholderMutation.graphql";
import type { dividendsPage_ShareholderDeleteMutation } from "@app/queries/dividendsPage_ShareholderDeleteMutation.graphql";
import type { dividendsPage_HoldingMutation } from "@app/queries/dividendsPage_HoldingMutation.graphql";
import type { dividendsPage_HoldingDeleteMutation } from "@app/queries/dividendsPage_HoldingDeleteMutation.graphql";
import type { dividendsPage_AssignMutation } from "@app/queries/dividendsPage_AssignMutation.graphql";
import type { dividendsPage_DateAddMutation } from "@app/queries/dividendsPage_DateAddMutation.graphql";
import type { dividendsPage_DateUpdateMutation } from "@app/queries/dividendsPage_DateUpdateMutation.graphql";
import type { dividendsPage_DateDeleteMutation } from "@app/queries/dividendsPage_DateDeleteMutation.graphql";
import type {
  dividendsPage_ClassifyMutation,
  TxnClassificationEnum,
} from "@app/queries/dividendsPage_ClassifyMutation.graphql";
import type { dividendsPage_PeriodTotalMutation } from "@app/queries/dividendsPage_PeriodTotalMutation.graphql";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  PieChart,
  Trash2,
  X,
} from "lucide-react";
import { startTransition, useEffect, useMemo, useRef, useState } from "react";
import { commitMutation, fetchQuery, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

export const dividendsQuery = graphql`
  query dividendsPage_Query {
    dividends {
      totalCents
      paidTotalCents
      unreconciledCount
      counterparties {
        id
        cxid
        name
      }
      shareholders {
        id
        handle
        name
        ordinal
        notes
        stakeholderCxid
        currentBasisPoints
        stakeholder {
          id
          name
        }
        holdings {
          id
          effectiveDate
          basisPoints
          note
        }
      }
      epochs {
        effectiveDate
        totalBps
        balanced
        holdings {
          basisPoints
          explicit
          shareholder {
            id
            name
          }
        }
      }
      periods {
        period
        startDate
        endDate
        totalCents
        paidTotalCents
        declaredTotalCents
        unaccountedCents
        paymentCount
        firstPaymentDate
        lastPaymentDate
        totalBps
        balanced
        unknownCents
        reconciled
        lines {
          basisPoints
          entitledCents
          paidCents
          varianceCents
          actualBps
          matched
          shareholder {
            id
            name
          }
        }
        payments {
          cents
          period
          derivedPeriod
          assigned
          shareholder {
            id
            name
          }
          row {
            txn {
              id
            }
            md5
            postingDate
            description
            amount
            accountNumber
            classification
            counterparty {
              name
            }
          }
        }
      }
      candidates {
        shareholder {
          id
          name
        }
        row {
          txn {
            id
          }
          md5
          postingDate
          description
          amount
          accountNumber
          classification
          counterparty {
            name
          }
        }
      }
    }
  }
`;

const shareholderMutation = graphql`
  mutation dividendsPage_ShareholderMutation($input: ShareholderUpsertInputObject!) {
    shareholderUpsert(input: $input) {
      shareholder {
        id
      }
      errors {
        message
      }
    }
  }
`;

const shareholderDeleteMutation = graphql`
  mutation dividendsPage_ShareholderDeleteMutation($id: ID!) {
    shareholderDelete(id: $id) {
      deleted
      errors {
        message
      }
    }
  }
`;

const holdingMutation = graphql`
  mutation dividendsPage_HoldingMutation($input: ShareholdingUpsertInputObject!) {
    shareholdingUpsert(input: $input) {
      shareholding {
        id
      }
      errors {
        message
      }
    }
  }
`;

const holdingDeleteMutation = graphql`
  mutation dividendsPage_HoldingDeleteMutation($id: ID!) {
    shareholdingDelete(id: $id) {
      deleted
      errors {
        message
      }
    }
  }
`;

const dateAddMutation = graphql`
  mutation dividendsPage_DateAddMutation($effectiveDate: ISO8601Date!) {
    capTableDateAdd(effectiveDate: $effectiveDate) {
      added
      errors {
        message
      }
    }
  }
`;

const dateUpdateMutation = graphql`
  mutation dividendsPage_DateUpdateMutation($from: ISO8601Date!, $to: ISO8601Date!) {
    capTableDateUpdate(from: $from, to: $to) {
      moved
      errors {
        message
      }
    }
  }
`;

const dateDeleteMutation = graphql`
  mutation dividendsPage_DateDeleteMutation($effectiveDate: ISO8601Date!) {
    capTableDateDelete(effectiveDate: $effectiveDate) {
      deleted
      errors {
        message
      }
    }
  }
`;

const assignMutation = graphql`
  mutation dividendsPage_AssignMutation($txnId: ID!, $period: String) {
    dividendAssignPeriod(txnId: $txnId, period: $period) {
      assigned
      errors {
        message
      }
    }
  }
`;

const classifyMutation = graphql`
  mutation dividendsPage_ClassifyMutation($input: ClassifyTxnsInputObject!) {
    classifyTxns(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

const periodTotalMutation = graphql`
  mutation dividendsPage_PeriodTotalMutation($period: String!, $totalCents: Int) {
    dividendSetPeriodTotal(period: $period, totalCents: $totalCents) {
      saved
      errors {
        message
      }
    }
  }
`;

type Dividends = dividendsPage_Query["response"]["dividends"];
type Shareholder = Dividends["shareholders"][number];
type Period = Dividends["periods"][number];
type Candidate = Dividends["candidates"][number];
type Epoch = Dividends["epochs"][number];
type Counterparty = Dividends["counterparties"][number];

const FULL_BPS = 10_000;

const money = (dollars: number) =>
  dollars.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const moneyCents = (cents: number) => money(cents / 100);
const pct = (bps: number | null | undefined) =>
  bps == null ? "—" : `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;
const day = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

// A transport failure has no server message to show, and some carry an empty one —
// which would toast a blank bar and read as nothing having happened.
const networkMessage = (error: Error) =>
  error.message || "Something went wrong — please try again.";

// What a settled classify batch says it did. Written from the batch rather than the
// click, so a run of clicks that coalesced into one request reports one outcome.
const classifyMessage = (count: number, classification: TxnClassificationEnum | null) => {
  if (classification === "DIVIDEND") {
    return count === 1
      ? "Flagged as a dividend — it no longer counts as a cost."
      : `${count} payments flagged as dividends — they no longer count as costs.`;
  }
  return count === 1
    ? "No longer a dividend — it counts as an ordinary payment again."
    : `${count} payments are no longer dividends — they count as ordinary payments again.`;
};

// A quarter's anchor month, so the reconciliation can be charted on the shared
// month-keyed KpiChart ("2025-Q2" -> "2025-04").
const periodToMonth = (period: string) => {
  const [year, quarter] = period.split("-Q");
  return `${year}-${String((Number(quarter) - 1) * 3 + 1).padStart(2, "0")}`;
};
const monthToPeriod = (month: string) => {
  const [year, m] = month.split("-");
  return `${year}-Q${Math.floor((Number(m) - 1) / 3) + 1}`;
};

// Distinguishable at a glance and stable per shareholder across both charts —
// ownership and payout have to be readable against each other.
const SERIES_COLORS = [
  "#7c3aed", "#0891b2", "#ea580c", "#16a34a", "#db2777",
  "#ca8a04", "#4f46e5", "#0d9488", "#dc2626", "#65a30d",
];

export default function DividendsPage({ queryRef }: { queryRef: PreloadedQuery<dividendsPage_Query> }) {
  const preloaded = usePreloadedQuery(dividendsQuery, queryRef);
  const [data, setData] = useState(preloaded);
  const { shareholders, counterparties, epochs, periods, candidates, totalCents, paidTotalCents, unreconciledCount } =
    data.dividends;
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());

  // A refetch invalidates the selection for the rows that have LEFT the worklist —
  // holding on to their ids would let a second click re-send them. Ids still on the
  // list survive it, because a flag settling in the background must not wipe a
  // selection the operator is in the middle of building.
  useEffect(() => {
    const live = new Set(candidates.map((candidate) => candidate.row.txn.id));
    setSelected((current) => {
      const next = new Set([...current].filter((id) => live.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [candidates]);

  // Re-sync the local mirror only when the route hands a NEW query, keyed on
  // queryRef — NOT on `preloaded`: refresh() writes the same query root into the
  // store, and keying on `preloaded` would re-emit the stale snapshot and clobber
  // the fresh numbers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setData(preloaded), [queryRef]);

  // Bumped on every refetch. The timeline is keyed on it so the whole grid
  // remounts against server truth after any write — a date the server REFUSED to
  // move to (its target was taken) would otherwise sit in the field looking
  // saved, since the epoch it belongs to never changed and nothing would tell the
  // input to go back.
  const [revision, setRevision] = useState(0);

  // THE REFETCH, COALESCED.
  //
  // Every mutation here can move numbers the whole page derives from (flagging a
  // payment re-classifies a txn, which moves EBITDA and the cost base), so they all
  // end by refetching rather than patching the Relay store — ON FAILURE TOO, since
  // classifyTxns commits its write and THEN re-derives downstream: a failure in
  // that tail still changed the database, and leaving the pre-save view up would
  // misreport what it now holds.
  //
  // That refetch is the whole page (cap table, every quarter, every payment, the
  // worklist), and it used to run once per write with the UI frozen behind it. Now
  // that a row no longer waits its turn, seven of them can land at once — so a
  // request that arrives while one is in flight sets a flag instead of stacking, and
  // exactly ONE more fetch runs when the current one lands. Seven writes cost two
  // refetches, and the last one still sees every write, because it starts after all
  // of them committed.
  const refreshing = useRef(false);
  const refreshAgain = useRef(false);
  const refreshWaiters = useRef<(() => void)[]>([]);

  const runRefresh = () => {
    refreshing.current = true;
    fetchQuery<dividendsPage_Query>(environment, dividendsQuery, {})
      .toPromise()
      .then((fresh) => {
        // A transition: this render redraws every quarter and both charts, and it
        // must not be allowed to swallow the next click while it does.
        startTransition(() => {
          if (fresh) setData(fresh);
          setRevision((value) => value + 1);
        });
      })
      // Rows hold their spinner until the refetch that will remove them lands, so a
      // refetch that never lands has to say so rather than leaving them spinning.
      .catch(() =>
        toast({ message: "Could not refresh — reload the page to see the latest.", kind: "error" })
      )
      .finally(() => {
        refreshing.current = false;
        if (refreshAgain.current) {
          refreshAgain.current = false;
          runRefresh();
          return;
        }
        const waiters = refreshWaiters.current;
        refreshWaiters.current = [];
        waiters.forEach((resolve) => resolve());
      });
  };

  // Resolves once a refetch that STARTED AFTER this call has landed — the caller is
  // asking to see its own write, and one already in flight may predate it.
  const refresh = () =>
    new Promise<void>((resolve) => {
      refreshWaiters.current.push(resolve);
      if (refreshing.current) refreshAgain.current = true;
      else runRefresh();
    });

  const settle = (errors: readonly { readonly message: string }[] | undefined, message: string) => {
    const failure = errors?.[0]?.message;
    toast({ message: failure ?? message, kind: failure ? "error" : "success" });
    refresh().finally(() => setBusy(null));
  };

  const fail = (error: Error) => {
    toast({ message: networkMessage(error), kind: "error" });
    refresh().finally(() => setBusy(null));
  };

  const saveShareholder = (
    input: dividendsPage_ShareholderMutation["variables"]["input"],
    key: string
  ) => {
    setBusy(key);
    commitMutation<dividendsPage_ShareholderMutation>(environment, {
      mutation: shareholderMutation,
      variables: { input },
      onCompleted: (response, errors) =>
        settle(errors ?? response.shareholderUpsert?.errors, "Cap table saved."),
      onError: fail,
    });
  };

  const deleteShareholder = (shareholder: Shareholder) => {
    setBusy(`delete:${shareholder.id}`);
    commitMutation<dividendsPage_ShareholderDeleteMutation>(environment, {
      mutation: shareholderDeleteMutation,
      variables: { id: shareholder.id },
      onCompleted: (response, errors) =>
        settle(
          errors ?? response.shareholderDelete?.errors,
          `${shareholder.name} removed from the cap table.`
        ),
      onError: fail,
    });
  };

  const saveHolding = (input: dividendsPage_HoldingMutation["variables"]["input"], key: string) => {
    setBusy(key);
    commitMutation<dividendsPage_HoldingMutation>(environment, {
      mutation: holdingMutation,
      variables: { input },
      onCompleted: (response, errors) =>
        settle(errors ?? response.shareholdingUpsert?.errors, "Ownership updated."),
      onError: fail,
    });
  };

  const deleteHolding = (id: string) => {
    setBusy(`holding:${id}`);
    commitMutation<dividendsPage_HoldingDeleteMutation>(environment, {
      mutation: holdingDeleteMutation,
      variables: { id },
      onCompleted: (response, errors) =>
        settle(errors ?? response.shareholdingDelete?.errors, "Ownership change removed."),
      onError: fail,
    });
  };

  const addDate = (effectiveDate: string) => {
    setBusy("date:add");
    commitMutation<dividendsPage_DateAddMutation>(environment, {
      mutation: dateAddMutation,
      variables: { effectiveDate },
      onCompleted: (response, errors) =>
        settle(errors ?? response.capTableDateAdd?.errors, `${day(effectiveDate)} added to the timeline.`),
      onError: fail,
    });
  };

  const moveDate = (from: string, to: string) => {
    setBusy(`date:${from}`);
    commitMutation<dividendsPage_DateUpdateMutation>(environment, {
      mutation: dateUpdateMutation,
      variables: { from, to },
      onCompleted: (response, errors) =>
        settle(errors ?? response.capTableDateUpdate?.errors, `Moved to ${day(to)}.`),
      onError: fail,
    });
  };

  const deleteDate = (effectiveDate: string) => {
    setBusy(`date:${effectiveDate}`);
    commitMutation<dividendsPage_DateDeleteMutation>(environment, {
      mutation: dateDeleteMutation,
      variables: { effectiveDate },
      onCompleted: (response, errors) =>
        settle(errors ?? response.capTableDateDelete?.errors, `${day(effectiveDate)} removed.`),
      onError: fail,
    });
  };

  // ---------------------------------------------------------------------------
  // ROW-LEVEL WRITES
  //
  // The three controls on a payment row — move it to another quarter, take the
  // dividend flag off it, put one on — used to run through the page-wide `busy`
  // gate, which disabled EVERY control on the page for the duration of one write
  // and refetched the whole page before releasing it. Clicking "Not a dividend" on
  // seven rows meant seven waits, and six of the clicks landed on a dead button and
  // were simply lost.
  //
  // Each of these now reports on its OWN row and takes the click immediately. The
  // two differ in how they reach the server, because the two mutations behind them
  // are not the same shape of work — see each one below.
  // ---------------------------------------------------------------------------

  // Rows with a quarter change in flight, and the quarter the user picked, so the
  // select shows the new value under the cursor rather than snapping back until the
  // refetch lands. `seq` keeps a slow first request from clearing the spinner on a
  // second change the user has since made to the same row.
  const [assigning, setAssigning] = useState<ReadonlyMap<string, { period: string; seq: number }>>(
    () => new Map()
  );
  const assignSeq = useRef(0);

  // Genuinely parallel, and safe to be: dividendAssignPeriod writes ONE row keyed by
  // the payment's md5 and derives nothing. Seven of them touch seven different rows.
  const assignPeriod = (txnId: string, period: string | null, shown: string) => {
    const seq = (assignSeq.current += 1);
    setAssigning((current) => new Map(current).set(txnId, { period: shown, seq }));

    const done = (errors: readonly { readonly message: string }[] | undefined, message: string) => {
      const failure = errors?.[0]?.message;
      toast({ message: failure ?? message, kind: failure ? "error" : "success" });
      // Held until the page has actually caught up. Dropping it on the server's
      // reply would snap the select back to the old quarter for as long as the
      // refetch takes, which reads as the change having been rejected.
      refresh().finally(() =>
        setAssigning((current) => {
          if (current.get(txnId)?.seq !== seq) return current;
          const next = new Map(current);
          next.delete(txnId);
          return next;
        })
      );
    };

    commitMutation<dividendsPage_AssignMutation>(environment, {
      mutation: assignMutation,
      variables: { txnId, period },
      onCompleted: (response, errors) =>
        done(
          errors ?? response.dividendAssignPeriod?.errors,
          period ? `Moved to ${period}.` : "Back to the quarter its date settles."
        ),
      onError: (error) => done([{ message: networkMessage(error) }], ""),
    });
  };

  // Rows whose classification the user has changed but the server has not confirmed.
  const [classifying, setClassifying] = useState<ReadonlySet<string>>(() => new Set());

  // NOT parallel on the wire, and deliberately so. classifyTxns re-derives the
  // account's operating verdict, normalized streams and general ledger — an
  // account-wide chain in one transaction. Two of them at once contend on the same
  // rows, and each can read a snapshot taken before the other's write committed, so
  // the loser's change is missing from the derived layer until the next edit. Firing
  // seven would be slower AND leave the ledger disagreeing with the flags.
  //
  // The click is still never blocked. It is accepted the instant it happens — the
  // row says so — and clicks that arrive while a request is in flight are COALESCED
  // into the next one. classifyTxns already takes a LIST and re-derives the batch
  // once, so seven rapid clicks cost two round trips and two derives instead of
  // seven of each, and finish sooner than seven parallel requests could have.
  const classifyQueue = useRef<{ classification: TxnClassificationEnum | null; txnIds: string[] }[]>([]);
  const classifyInFlight = useRef(false);

  const classify = (txnIds: readonly string[], classification: TxnClassificationEnum | null) => {
    if (txnIds.length === 0) return;
    setClassifying((current) => new Set([...current, ...txnIds]));

    // Only a run of clicks meaning the SAME thing can share a request; a flag and an
    // unflag are different values for the same column and cannot travel together.
    const tail = classifyQueue.current.at(-1);
    if (tail && tail.classification === classification) tail.txnIds.push(...txnIds);
    else classifyQueue.current.push({ classification, txnIds: [...txnIds] });

    pumpClassify();
  };

  const pumpClassify = () => {
    if (classifyInFlight.current) return;
    const batch = classifyQueue.current.shift();
    if (!batch) return;

    classifyInFlight.current = true;
    const done = (errors: readonly { readonly message: string }[] | undefined) => {
      classifyInFlight.current = false;
      const failure = errors?.[0]?.message;
      toast({
        message: failure ?? classifyMessage(batch.txnIds.length, batch.classification),
        kind: failure ? "error" : "success",
      });
      // The next batch goes out NOW — it does not wait for the refetch, only for
      // the derive. The rows keep their spinner until the page has caught up and
      // they have left the list, so they never flash back into place first.
      pumpClassify();
      refresh().finally(() =>
        setClassifying((current) => {
          const next = new Set(current);
          batch.txnIds.forEach((id) => next.delete(id));
          return next;
        })
      );
    };

    commitMutation<dividendsPage_ClassifyMutation>(environment, {
      mutation: classifyMutation,
      variables: { input: { txnIds: batch.txnIds, classification: batch.classification } },
      onCompleted: (response, errors) => done(errors ?? response.classifyTxns?.errors),
      onError: (error) => done([{ message: networkMessage(error) }]),
    });
  };

  // Quarters whose declared total is in flight, holding the figure the user just
  // entered (null = they cleared it) so the number changes under the cursor instead
  // of after a round trip. Parallel like the quarter changes, and for the same
  // reason: one row keyed by the quarter, nothing derived.
  const [declaring, setDeclaring] = useState<ReadonlyMap<string, number | null>>(() => new Map());

  const setPeriodTotal = (period: string, totalCents: number | null) => {
    setDeclaring((current) => new Map(current).set(period, totalCents));

    const done = (errors: readonly { readonly message: string }[] | undefined) => {
      const failure = errors?.[0]?.message;
      toast({
        message:
          failure ??
          (totalCents === null
            ? `${period.replace("-", " ")} is back on what the feed shows.`
            : `${period.replace("-", " ")} distributed ${moneyCents(totalCents)}.`),
        kind: failure ? "error" : "success",
      });
      // Held past the reply for the same reason the others are: the entitlements
      // below only recompute when the refetch lands, and the figure they are a
      // share of must not change back before they do.
      refresh().finally(() =>
        setDeclaring((current) => {
          const next = new Map(current);
          next.delete(period);
          return next;
        })
      );
    };

    commitMutation<dividendsPage_PeriodTotalMutation>(environment, {
      mutation: periodTotalMutation,
      variables: { period, totalCents },
      onCompleted: (response, errors) => done(errors ?? response.dividendSetPeriodTotal?.errors),
      onError: (error) => done([{ message: networkMessage(error) }]),
    });
  };

  // WHICH QUARTERS ARE OPEN.
  //
  // Held here rather than inside each card so "Collapse all" can reach them, and
  // held as an OVERRIDE of the default (a quarter that does not reconcile opens
  // itself, because that is the one you came here for) rather than as the state
  // itself. Refetches are frequent now, and a plain set seeded from `periods` would
  // reopen every flagged quarter under the operator every time one landed.
  const [openPeriods, setOpenPeriods] = useState<ReadonlyMap<string, boolean>>(() => new Map());
  const periodOpen = (period: Period) => openPeriods.get(period.period) ?? !period.reconciled;
  const anyPeriodOpen = periods.some(periodOpen);

  const togglePeriod = (period: Period) =>
    setOpenPeriods((current) => new Map(current).set(period.period, !periodOpen(period)));

  const toggleAllPeriods = () =>
    setOpenPeriods(new Map(periods.map((period) => [period.period, !anyPeriodOpen])));

  // Ownership over time, as a stacked % chart. Each epoch is a complete split, so
  // the series is a step function: an epoch's split holds until the next one.
  const ownershipSeries = useMemo(() => {
    if (epochs.length === 0) return [];
    const months = ownershipMonths(epochs.map((epoch) => epoch.effectiveDate), periods);
    return shareholders.map((shareholder, index) => ({
      name: shareholder.name,
      type: "bar" as const,
      stack: "ownership",
      color: SERIES_COLORS[index % SERIES_COLORS.length],
      points: months.map((month) => {
        const epoch = epochInForce(epochs, month);
        const holding = epoch?.holdings.find((h) => h.shareholder.id === shareholder.id);
        return { month, value: holding ? holding.basisPoints / FULL_BPS : 0 };
      }),
    }));
  }, [epochs, shareholders, periods]);

  // What each owner was actually paid, per quarter — the same stack, in dollars,
  // so a distribution that went out on the wrong split is visible as a stack that
  // does not look like the one above it.
  const payoutSeries = useMemo(() => {
    if (periods.length === 0) return [];
    const months = periods.map((period) => periodToMonth(period.period)).sort();
    return shareholders.map((shareholder, index) => ({
      name: shareholder.name,
      type: "bar" as const,
      stack: "payout",
      color: SERIES_COLORS[index % SERIES_COLORS.length],
      points: months.map((month) => {
        const period = periods.find((p) => periodToMonth(p.period) === month);
        const line = period?.lines.find((l) => l.shareholder?.id === shareholder.id);
        return { month, value: (line?.paidCents ?? 0) / 100 };
      }),
    }));
  }, [periods, shareholders]);

  // BOTH CHARTS, HELD AS ELEMENTS.
  //
  // These are echarts instances, and rebuilding them is by far the most expensive
  // thing this page renders. Every per-row pending flag lives in page state, so
  // without this a single click re-rendered both charts BEFORE the spinner could
  // paint — long enough that clicking a second row felt like nothing had happened.
  // Memoising the elements (not just the series) lets React skip the subtree
  // entirely: a click now repaints the rows and leaves the charts alone.
  const ownershipChart = useMemo(
    () =>
      ownershipSeries.length === 0 ? null : (
        <KpiChart
          title="Ownership over time"
          series={ownershipSeries}
          format="percent"
          formatXLabel={(month) => monthToPeriod(month).replace("-", " ")}
          onMonthClick={() => {}}
        />
      ),
    [ownershipSeries]
  );

  const payoutChart = useMemo(
    () =>
      payoutSeries.length === 0 ? null : (
        <KpiChart
          title="Distributed per quarter, by shareholder"
          series={payoutSeries}
          format="money"
          formatXLabel={(month) => monthToPeriod(month).replace("-", " ")}
          onMonthClick={() => {}}
        />
      ),
    [payoutSeries]
  );

  const latestEpoch = epochs.at(-1);
  const capTableBalanced = latestEpoch?.balanced ?? true;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div>
        <h1 className="text-xl font-bold">Dividends &amp; ownership</h1>
        <p className="text-sm text-gray-500">
          Who owns the business, what has been distributed to them, and whether the two agree. A
          dividend is profit going out, not a cost of running the company — flagged payments are
          held out of expenses, EBITDA and CAC everywhere in the app, and shown on their own line
          below the bottom line.
        </p>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {/* Once a quarter declares a total, this figure is no longer purely what the
            feed shows — say so, rather than letting the headline quietly change meaning. */}
        <SummaryCard
          label="Distributed, all time"
          value={moneyCents(totalCents)}
          tone="text-violet-700"
          hint={
            totalCents === paidTotalCents
              ? undefined
              : `${moneyCents(paidTotalCents)} of it is in the feed; the rest is declared`
          }
        />
        <SummaryCard label="Quarters distributed" value={String(periods.length)} />
        <SummaryCard
          label="Quarters that don't reconcile"
          value={String(unreconciledCount)}
          tone={unreconciledCount > 0 ? "text-amber-600" : "text-emerald-600"}
        />
        <SummaryCard
          label="Cap table today"
          value={latestEpoch ? pct(latestEpoch.totalBps) : "—"}
          tone={capTableBalanced ? "text-gray-900" : "text-amber-600"}
        />
      </div>

      {!capTableBalanced && latestEpoch && (
        <Banner>
          The cap table adds up to {pct(latestEpoch.totalBps)}, not 100%. Every entitlement below is
          computed from these stakes as they stand, so they will not sum to what was paid out until
          this is fixed.
        </Banner>
      )}

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold">Ownership timeline</h2>
          <p className="text-sm text-gray-500">
            One row per date the cap table changed, one column per shareholder. Edit a stake in
            place, correct a date that was recorded on the wrong day, or add the date of a change
            that is not on the record yet. A row that does not add up to 100% is flagged — every
            entitlement below is computed from these stakes exactly as they stand.
          </p>
        </div>
        <CapTableTimeline
          key={revision}
          epochs={epochs}
          shareholders={shareholders}
          busy={busy}
          onSetCell={(shareholderId, effectiveDate, basisPoints) =>
            saveHolding({ shareholderId, effectiveDate, basisPoints }, `cell:${effectiveDate}`)
          }
          onMoveDate={moveDate}
          onDeleteDate={deleteDate}
          onAddDate={addDate}
        />
      </section>

      {ownershipChart}
      {payoutChart}

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold">Cap table</h2>
          <p className="text-sm text-gray-500">
            Who owns the business and since when. Ownership is stored as changes, not as a single
            current number — add a row on the date a stake moved and the stake before it stays on
            the record, so a dividend paid in 2022 is still checked against the 2022 split.
          </p>
        </div>
        <div className="space-y-3">
          {shareholders.map((shareholder) => (
            <ShareholderCard
              key={shareholder.id}
              shareholder={shareholder}
              counterparties={counterparties}
              busy={busy}
              onSave={(input) => saveShareholder(input, `save:${shareholder.id}`)}
              onDelete={() => deleteShareholder(shareholder)}
              onSaveHolding={(input) => saveHolding(input, `holding:${shareholder.id}`)}
              onDeleteHolding={deleteHolding}
            />
          ))}
          <AddShareholder busy={busy} onAdd={(input) => saveShareholder(input, "add")} />
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <h2 className="text-lg font-semibold">Quarterly distributions</h2>
            <p className="text-sm text-gray-500">
              Each quarter's payments, split by recipient, against what the cap table in force at
              the end of that quarter says each owner was owed. A quarter distributes what its
              payments add up to unless you tell it otherwise — click the amount to declare what
              really went out, and the entitlements below it are recomputed from that.
            </p>
          </div>
          {periods.length > 0 && (
            <button
              type="button"
              className="shrink-0 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
              onClick={toggleAllPeriods}
            >
              {anyPeriodOpen ? "Collapse all" : "Expand all"}
            </button>
          )}
        </div>
        {periods.length === 0 ? (
          <p className="bg-white rounded border p-6 text-sm text-gray-500">
            Nothing distributed yet. Payments show up here once they are flagged as dividends —
            check the list below for ones that may be sitting in payroll.
          </p>
        ) : (
          <div className="space-y-2">
            {periods.map((period) => (
              <PeriodCard
                key={period.period}
                period={period}
                open={periodOpen(period)}
                onToggle={() => togglePeriod(period)}
                assigning={assigning}
                classifying={classifying}
                declaring={declaring}
                onAssign={assignPeriod}
                onUnflag={(txnId) => classify([txnId], null)}
                onSetTotal={setPeriodTotal}
              />
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold">Payments to owners not counted as dividends</h2>
          <p className="text-sm text-gray-500">
            Money that went to someone on the cap table but is still being treated as an ordinary
            expense. A draw sitting under salary depresses profit by its full amount — flag it and
            it moves out of costs and into the reconciliation above.
          </p>
        </div>
        {candidates.length === 0 ? (
          <p className="bg-white rounded border p-6 text-sm text-gray-500">
            {shareholders.some((s) => s.stakeholderCxid)
              ? "Nothing to triage — every payment to an owner is accounted for."
              : "Link each shareholder to the counterparty they are paid as (above) and any unflagged draws will appear here."}
          </p>
        ) : (
          <CandidateTable
            candidates={candidates}
            classifying={classifying}
            selected={selected}
            onSelect={setSelected}
            onFlag={(txnIds) => classify(txnIds, "DIVIDEND")}
          />
        )}
      </section>
    </div>
  );
}

// THE OWNERSHIP TIMELINE, editable in place: dates down the side, shareholders
// across the top.
//
// Ownership is stored as change EVENTS, so a row is a DATE and a cell is the
// stake in force for that shareholder from it. A cell not recorded on its own
// row's date was carried forward from an earlier one, and is drawn plain and grey
// rather than as a filled-in field. Typing into one records a real change on that
// date — which is what the grid is for — but you should be able to see which
// decisions are already on the record before making another one.
//
// A row that does not total 100% is flagged rather than corrected. Every
// entitlement on this page is computed from these stakes exactly as they stand,
// so normalising a 96% row away would hide the bug and make the reconciliation
// below look clean while it quietly paid out against the wrong split.
function CapTableTimeline({
  epochs,
  shareholders,
  busy,
  onSetCell,
  onMoveDate,
  onDeleteDate,
  onAddDate,
}: {
  epochs: readonly Epoch[];
  shareholders: readonly Shareholder[];
  busy: string | null;
  onSetCell: (shareholderId: string, effectiveDate: string, basisPoints: number) => void;
  onMoveDate: (from: string, to: string) => void;
  onDeleteDate: (effectiveDate: string) => void;
  onAddDate: (effectiveDate: string) => void;
}) {
  const [newDate, setNewDate] = useState("");

  if (shareholders.length === 0) {
    return (
      <p className="bg-white rounded border p-6 text-sm text-gray-500">
        Add a shareholder below and their ownership over time becomes editable here.
      </p>
    );
  }

  const add = () => {
    if (!newDate) return;
    onAddDate(newDate);
    setNewDate("");
  };

  return (
    <div className="bg-white rounded border overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b bg-gray-50">
            <th className="px-4 py-2 font-medium">From</th>
            {shareholders.map((shareholder) => (
              <th key={shareholder.id} className="px-3 py-2 font-medium text-right">
                {shareholder.name}
              </th>
            ))}
            <th className="px-3 py-2 font-medium text-right">Total</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {epochs.map((epoch) => (
            <tr
              key={epoch.effectiveDate}
              className={`border-b last:border-0 ${epoch.balanced ? "" : "bg-red-50"}`}
            >
              <td className="px-4 py-1.5">
                <input
                  type="date"
                  className={inputClass}
                  defaultValue={epoch.effectiveDate}
                  disabled={busy !== null}
                  onChange={(event) =>
                    event.target.value &&
                    event.target.value !== epoch.effectiveDate &&
                    onMoveDate(epoch.effectiveDate, event.target.value)
                  }
                />
              </td>
              {shareholders.map((shareholder) => {
                const holding = epoch.holdings.find((held) => held.shareholder.id === shareholder.id);
                return (
                  <td key={shareholder.id} className="px-3 py-1.5 text-right">
                    <PercentCell
                      key={`${epoch.effectiveDate}:${holding?.basisPoints ?? "none"}`}
                      basisPoints={holding?.basisPoints ?? null}
                      explicit={holding?.explicit ?? false}
                      disabled={busy !== null}
                      onCommit={(basisPoints) =>
                        onSetCell(shareholder.id, epoch.effectiveDate, basisPoints)
                      }
                    />
                  </td>
                );
              })}
              <td
                className={`px-3 py-1.5 text-right tabular-nums ${
                  epoch.balanced ? "text-gray-500" : "text-red-600 font-semibold"
                }`}
              >
                {pct(epoch.totalBps)}
              </td>
              <td className="px-3 py-1.5 text-right">
                <button
                  type="button"
                  className="text-gray-400 hover:text-red-600"
                  disabled={busy !== null}
                  onClick={() => onDeleteDate(epoch.effectiveDate)}
                  title="Remove this date — the split before it carries forward again"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </td>
            </tr>
          ))}
          <tr>
            <td className="px-4 py-2">
              <input
                type="date"
                className={inputClass}
                value={newDate}
                disabled={busy !== null}
                onChange={(event) => setNewDate(event.target.value)}
              />
            </td>
            <td colSpan={shareholders.length + 2} className="px-3 py-2">
              <button
                type="button"
                className={buttonClass}
                disabled={busy !== null || !newDate}
                onClick={add}
              >
                Add this date
              </button>
              <span className="ml-3 text-xs text-gray-400">
                Starts from the split in force on that date — then change the stakes that moved.
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// One stake, typed as a percent. Commits on blur or Enter, never per keystroke —
// otherwise every "5" on the way to "50" writes a holding and re-derives the page.
function PercentCell({
  basisPoints,
  explicit,
  disabled,
  onCommit,
}: {
  basisPoints: number | null;
  explicit: boolean;
  disabled: boolean;
  onCommit: (basisPoints: number) => void;
}) {
  const [draft, setDraft] = useState(basisPoints == null ? "" : String(basisPoints / 100));

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === "") return;
    const next = Math.round(parseFloat(trimmed) * 100);
    if (Number.isNaN(next) || next === basisPoints) return;
    onCommit(next);
  };

  return (
    <input
      type="number"
      step="0.01"
      value={draft}
      disabled={disabled}
      placeholder="—"
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => event.key === "Enter" && event.currentTarget.blur()}
      className={`w-24 rounded border px-2 py-1 text-sm text-right tabular-nums focus:border-violet-400 focus:outline-none disabled:opacity-50 ${
        explicit ? "border-gray-200 text-gray-900" : "border-transparent text-gray-400"
      }`}
    />
  );
}

// The month spine the ownership chart is drawn on: every quarter from the first
// ownership change to the later of the last change and the last distribution, so
// the two charts line up column for column.
function ownershipMonths(changeDates: readonly string[], periods: readonly Period[]): string[] {
  const quarters = new Set<string>();
  const first = changeDates[0];
  if (!first) return [];

  const start = new Date(`${first}T00:00:00`);
  const endCandidates = [
    ...changeDates.map((date) => new Date(`${date}T00:00:00`)),
    ...periods.map((period) => new Date(`${period.endDate}T00:00:00`)),
    new Date(),
  ];
  const end = new Date(Math.max(...endCandidates.map((date) => date.getTime())));

  const cursor = new Date(start.getFullYear(), Math.floor(start.getMonth() / 3) * 3, 1);
  while (cursor <= end) {
    quarters.add(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`);
    cursor.setMonth(cursor.getMonth() + 3);
  }
  return [...quarters].sort();
}

function epochInForce(epochs: Dividends["epochs"], month: string) {
  const asOf = `${month}-01`;
  return [...epochs].reverse().find((epoch) => epoch.effectiveDate <= asOf);
}

function SummaryCard({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: string;
  tone?: string;
  hint?: string;
}) {
  return (
    <div className="bg-white rounded border p-4">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className={`text-2xl font-bold ${tone ?? "text-gray-900"}`}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-violet-600">{hint}</div>}
    </div>
  );
}

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
}

// One cap-table entry: the identity (name, and the counterparty they are paid as)
// plus the whole history of what they have held. The history is editable in place
// because that is where the mistakes are — a stake typed against the wrong date is
// invisible until a distribution reconciles badly three quarters later.
function ShareholderCard({
  shareholder,
  counterparties,
  busy,
  onSave,
  onDelete,
  onSaveHolding,
  onDeleteHolding,
}: {
  shareholder: Shareholder;
  counterparties: readonly Counterparty[];
  busy: string | null;
  onSave: (input: dividendsPage_ShareholderMutation["variables"]["input"]) => void;
  onDelete: () => void;
  onSaveHolding: (input: dividendsPage_HoldingMutation["variables"]["input"]) => void;
  onDeleteHolding: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(shareholder.name);
  const [cxid, setCxid] = useState(shareholder.stakeholderCxid ?? "");
  const [notes, setNotes] = useState(shareholder.notes ?? "");
  const [date, setDate] = useState("");
  const [percent, setPercent] = useState("");
  const [note, setNote] = useState("");

  const saving = busy === `save:${shareholder.id}`;
  const holdings = [...shareholder.holdings].sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));

  const addHolding = () => {
    if (!date || percent === "") return;
    onSaveHolding({
      shareholderId: shareholder.id,
      effectiveDate: date,
      basisPoints: Math.round(parseFloat(percent) * 100),
      note: note || null,
    });
    setDate("");
    setPercent("");
    setNote("");
  };

  return (
    <div className="bg-white rounded border">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50"
      >
        {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
        <PieChart className="h-4 w-4 text-violet-600" />
        <span className="font-medium">{shareholder.name}</span>
        <span className="text-sm text-gray-500">
          {shareholder.currentBasisPoints == null
            ? "no stake on record"
            : `${pct(shareholder.currentBasisPoints)} today`}
        </span>
        <span className="ml-auto text-xs text-gray-400">
          {shareholder.stakeholder
            ? `paid as ${shareholder.stakeholder.name}`
            : "not linked to a counterparty"}
        </span>
      </button>

      {open && (
        <div className="border-t p-4 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Name">
              <input
                className={inputClass}
                value={name}
                onChange={(event) => setName(event.target.value)}
                onBlur={() => name !== shareholder.name && onSave({ handle: shareholder.handle, name })}
              />
            </Field>
            <Field label="Paid as (counterparty)" hint="How their dividends are attributed">
              <select
                className={inputClass}
                value={cxid}
                onChange={(event) => {
                  setCxid(event.target.value);
                  onSave({ handle: shareholder.handle, stakeholderCxid: event.target.value || null });
                }}
              >
                <option value="">— not linked —</option>
                {counterparties.map((counterparty) => (
                  <option key={counterparty.id} value={counterparty.cxid}>
                    {counterparty.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Notes">
              <input
                className={inputClass}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                onBlur={() => notes !== (shareholder.notes ?? "") && onSave({ handle: shareholder.handle, notes })}
              />
            </Field>
          </div>

          <div>
            <div className="text-xs font-medium text-gray-500 mb-2">Ownership history</div>
            {holdings.length === 0 ? (
              <p className="text-sm text-gray-400 mb-2">
                No stake on record. Add the date they acquired their shares and how much they held.
              </p>
            ) : (
              <table className="w-full text-sm mb-2">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-1 font-medium">From</th>
                    <th className="py-1 font-medium">Stake</th>
                    <th className="py-1 font-medium">What happened</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {holdings.map((holding) => (
                    <tr key={holding.id} className="border-b last:border-0">
                      <td className="py-1.5">{day(holding.effectiveDate)}</td>
                      <td className="py-1.5 font-medium tabular-nums">{pct(holding.basisPoints)}</td>
                      <td className="py-1.5 text-gray-500">{holding.note ?? "—"}</td>
                      <td className="py-1.5 text-right">
                        <button
                          type="button"
                          className="text-gray-400 hover:text-red-600"
                          disabled={busy !== null}
                          onClick={() => onDeleteHolding(holding.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="flex gap-2 items-end">
              <Field label="From">
                <input type="date" className={inputClass} value={date} onChange={(e) => setDate(e.target.value)} />
              </Field>
              <Field label="Stake %">
                <input
                  type="number"
                  step="0.01"
                  className={inputClass}
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                />
              </Field>
              <Field label="What happened">
                <input className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
              <button type="button" className={buttonClass} disabled={busy !== null} onClick={addHolding}>
                Add
              </button>
            </div>
          </div>

          <div className="flex justify-between items-center pt-2 border-t">
            <span className="text-xs text-gray-400">{saving ? "Saving…" : `handle: ${shareholder.handle}`}</span>
            <button
              type="button"
              className="text-sm text-red-600 hover:underline"
              disabled={busy !== null}
              onClick={onDelete}
            >
              Remove from cap table
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AddShareholder({
  busy,
  onAdd,
}: {
  busy: string | null;
  onAdd: (input: dividendsPage_ShareholderMutation["variables"]["input"]) => void;
}) {
  const [name, setName] = useState("");

  const add = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    // The handle is the portable identity the config replays by, so it is derived
    // from the name once and never re-derived — renaming someone must not orphan
    // their holdings.
    onAdd({ handle: trimmed.toLowerCase().replace(/[^a-z0-9]+/g, "-"), name: trimmed });
    setName("");
  };

  return (
    <div className="bg-white rounded border p-4 flex gap-2 items-end">
      <Field label="Add a shareholder">
        <input
          className={inputClass}
          placeholder="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && add()}
        />
      </Field>
      <button type="button" className={buttonClass} disabled={busy !== null} onClick={add}>
        Add
      </button>
    </div>
  );
}

// One quarter, reconciled. The header is the verdict; open it for the per-owner
// arithmetic and the individual payments. The payments carry the correction: a
// distribution paid either side of a quarter boundary settles two different
// quarters, and the row says whether it landed where its date put it or because
// someone moved it.
//
// Nothing in here waits on anything else in here. Every control reports on its own
// row and takes the click straight away — see the row-level writes on the page
// component for why one of them coalesces on the wire and the others do not.
function PeriodCard({
  period,
  open,
  onToggle,
  assigning,
  classifying,
  declaring,
  onAssign,
  onUnflag,
  onSetTotal,
}: {
  period: Period;
  open: boolean;
  onToggle: () => void;
  assigning: ReadonlyMap<string, { period: string; seq: number }>;
  classifying: ReadonlySet<string>;
  declaring: ReadonlyMap<string, number | null>;
  onAssign: (txnId: string, period: string | null, shown: string) => void;
  onUnflag: (txnId: string) => void;
  onSetTotal: (period: string, totalCents: number | null) => void;
}) {
  const spread =
    period.firstPaymentDate === period.lastPaymentDate
      ? day(period.firstPaymentDate)
      : `${day(period.firstPaymentDate)} → ${day(period.lastPaymentDate)}`;

  // The figure the operator just entered wins over the server's until the refetch
  // lands, so the number moves under the cursor rather than a round trip later.
  const saving = declaring.has(period.period);
  const pendingTotal = declaring.get(period.period);
  const totalCents = saving ? pendingTotal ?? period.paidTotalCents : period.totalCents;
  const declared = saving ? pendingTotal !== null : period.declaredTotalCents !== null;
  const unaccounted = totalCents - period.paidTotalCents;

  return (
    <div className="bg-white rounded border">
      <div className="flex items-center gap-3 px-4 py-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
          )}
          <span className="w-20 shrink-0 font-medium">{period.period.replace("-", " ")}</span>
          <span className="truncate text-sm text-gray-500">
            {period.paymentCount} payment{period.paymentCount === 1 ? "" : "s"} · {spread}
          </span>
        </button>

        {/* Fixed slots so the amounts and the verdicts each line up down the stack
            of quarters, rather than shifting with the width of the text before them. */}
        <span className="flex w-44 shrink-0 justify-end">
          <PeriodTotal
            totalCents={totalCents}
            declared={declared}
            saving={saving}
            onSet={(cents) => onSetTotal(period.period, cents)}
          />
        </span>

        <span className="w-48 shrink-0 text-right">
          {period.reconciled ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              <Check className="h-3 w-3" /> Reconciles
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
              <AlertTriangle className="h-3 w-3" /> {reasonFor(period)}
            </span>
          )}
        </span>
      </div>

      {open && (
        <div className="border-t px-4 py-3 space-y-3">
          {!period.balanced && (
            <Banner>
              The cap table for this quarter adds up to {pct(period.totalBps)}, not 100%. The
              entitlements below are computed from those stakes as they stand.
            </Banner>
          )}

          {declared && (
            <div className="rounded border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-900">
              Reconciled against {moneyCents(totalCents)}, declared by hand — the feed shows{" "}
              {moneyCents(period.paidTotalCents)}.{" "}
              {unaccounted > 0
                ? `The missing ${moneyCents(unaccounted)} leaves every owner below short by their share of it.`
                : unaccounted < 0
                  ? `That is ${moneyCents(-unaccounted)} LESS than the payments on record, so the quarter reads as over-paid.`
                  : "The two agree."}
            </div>
          )}

          <table className={`w-full text-sm ${saving ? "opacity-50" : ""}`}>
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="py-1 font-medium">Shareholder</th>
                <th className="py-1 font-medium text-right">Stake</th>
                <th className="py-1 font-medium text-right">Owed</th>
                <th className="py-1 font-medium text-right">Paid</th>
                <th className="py-1 font-medium text-right">Actual share</th>
                <th className="py-1 font-medium text-right">Difference</th>
              </tr>
            </thead>
            <tbody>
              {period.lines.map((line) => (
                <tr key={line.shareholder?.id ?? "unknown"} className="border-b last:border-0">
                  <td className="py-1">
                    {line.shareholder?.name ?? (
                      <span className="text-amber-700">Not on the cap table</span>
                    )}
                  </td>
                  <td className="py-1 text-right tabular-nums">{pct(line.basisPoints)}</td>
                  <td className="py-1 text-right tabular-nums text-gray-500">
                    {moneyCents(line.entitledCents)}
                  </td>
                  <td className="py-1 text-right tabular-nums font-medium">{moneyCents(line.paidCents)}</td>
                  <td className="py-1 text-right tabular-nums text-gray-500">{pct(line.actualBps)}</td>
                  <td
                    className={`py-1 text-right tabular-nums font-medium ${
                      line.matched ? "text-gray-400" : line.varianceCents < 0 ? "text-red-600" : "text-amber-600"
                    }`}
                  >
                    {line.matched && line.varianceCents === 0
                      ? "—"
                      : `${line.varianceCents > 0 ? "+" : ""}${moneyCents(line.varianceCents)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Payments</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-1 pr-3 font-medium">Date</th>
                  <th className="py-1 pr-3 font-medium">Description</th>
                  <th className="py-1 pr-3 font-medium">To</th>
                  <th className="py-1 pr-6 font-medium text-right">Amount</th>
                  <th className="py-1 pr-3 font-medium">Quarter</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {period.payments.map((payment) => (
                  <PaymentRow
                    key={payment.row.md5}
                    payment={payment}
                    assigning={assigning.get(payment.row.txn.id)}
                    unflagging={classifying.has(payment.row.txn.id)}
                    onAssign={onAssign}
                    onUnflag={onUnflag}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ONE PAYMENT, on its own. Both of its controls report here and nowhere else: a
// quarter change in flight shows the quarter the operator picked with a spinner
// beside it, and an unflag greys the row out on the click rather than when the
// server gets round to it. Neither disables the other, and neither disables
// anything on another row.
function PaymentRow({
  payment,
  assigning,
  unflagging,
  onAssign,
  onUnflag,
}: {
  payment: Period["payments"][number];
  assigning: { period: string; seq: number } | undefined;
  unflagging: boolean;
  onAssign: (txnId: string, period: string | null, shown: string) => void;
  onUnflag: (txnId: string) => void;
}) {
  const txnId = payment.row.txn.id;
  const shownPeriod = assigning?.period ?? payment.period;
  // Either kind of write in flight dims the WHOLE row — the row is the unit of
  // work, and dimming only the control you touched leaves the other five cells
  // looking live while the numbers behind them are already stale.
  const working = unflagging || assigning !== undefined;

  return (
    <tr
      className={`border-b last:border-0 transition-opacity ${working ? "opacity-50" : ""}`}
      aria-busy={working}
    >
      <td className="py-1 pr-3 whitespace-nowrap">{day(payment.row.postingDate)}</td>
      <td className="py-1 pr-3 max-w-md truncate text-gray-600" title={payment.row.description}>
        {payment.row.description}
      </td>
      <td className="py-1 pr-3">{payment.shareholder?.name ?? payment.row.counterparty?.name ?? "—"}</td>
      <td className="py-1 pr-6 text-right tabular-nums">{moneyCents(payment.cents)}</td>
      <td className="py-1 pr-3">
        <span className="inline-flex items-center gap-1">
          <select
            className="rounded border border-gray-200 bg-white px-1 py-0.5 text-xs disabled:opacity-60"
            value={shownPeriod}
            disabled={working}
            onChange={(event) =>
              onAssign(
                txnId,
                event.target.value === payment.derivedPeriod ? null : event.target.value,
                event.target.value
              )
            }
          >
            {neighbouringPeriods(payment.derivedPeriod, shownPeriod).map((option) => (
              <option key={option} value={option}>
                {option.replace("-", " ")}
                {option === payment.derivedPeriod ? " (default)" : ""}
              </option>
            ))}
          </select>
          {assigning ? (
            <Loader2 className="h-3 w-3 animate-spin text-violet-500" />
          ) : (
            payment.assigned && <span className="text-xs text-violet-600">moved</span>
          )}
        </span>
      </td>
      <td className="py-1 text-right">
        {/* The link keeps its label and grows a spinner, rather than being swapped
            for different text — the thing you clicked stays the thing you clicked. */}
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-red-600 disabled:cursor-default disabled:hover:text-gray-400"
          disabled={unflagging}
          onClick={() => onUnflag(txnId)}
        >
          {unflagging && <Loader2 className="h-3 w-3 animate-spin" />}
          Not a dividend
        </button>
      </td>
    </tr>
  );
}

// THE QUARTER'S TOTAL, click to correct.
//
// A quarter distributes what its payments add up to — until an operator says
// otherwise, because a distribution paid partly out of an account we do not ingest
// makes that sum a floor rather than the truth. Entering a figure here reconciles
// the owners below against it; clearing it hands the quarter back to the feed.
//
// Dollars in, cents out: nobody types 50000000 for half a million.
function PeriodTotal({
  totalCents,
  declared,
  saving,
  onSet,
}: {
  totalCents: number;
  declared: boolean;
  saving: boolean;
  onSet: (totalCents: number | null) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);

  const commit = () => {
    if (draft === null) return;
    const trimmed = draft.trim();

    // Emptying the field is how you take the declaration off, which is a different
    // act from cancelling — Escape does that.
    if (trimmed === "") {
      setDraft(null);
      setInvalid(false);
      if (declared) onSet(null);
      return;
    }

    const dollars = Number(trimmed.replace(/[$,\s]/g, ""));
    if (!Number.isFinite(dollars) || dollars <= 0) {
      setInvalid(true);
      return;
    }

    const cents = Math.round(dollars * 100);
    setDraft(null);
    setInvalid(false);
    if (cents !== totalCents || !declared) onSet(cents);
  };

  if (draft !== null) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
          setInvalid(false);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
          if (event.key === "Escape") {
            setDraft(null);
            setInvalid(false);
          }
        }}
        placeholder="Empty to use the feed"
        className={`w-full rounded border px-2 py-0.5 text-right text-sm tabular-nums focus:outline-none ${
          invalid ? "border-red-400 bg-red-50" : "border-violet-400"
        }`}
      />
    );
  }

  return (
    <span className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        title={declared ? "Declared by hand — click to correct" : "Click to declare what really went out"}
        onClick={() => setDraft(String(totalCents / 100))}
        className={`rounded px-1 font-bold tabular-nums hover:bg-violet-50 hover:text-violet-700 ${
          declared ? "text-violet-700 underline decoration-violet-300 decoration-dotted underline-offset-4" : ""
        }`}
      >
        {moneyCents(totalCents)}
      </button>
      {saving && <Loader2 className="h-3 w-3 animate-spin text-violet-500" />}
      {declared && !saving && (
        <button
          type="button"
          title="Back to what the payments add up to"
          className="text-gray-300 hover:text-red-600"
          onClick={() => onSet(null)}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

// Why a quarter is flagged, in the fewest words that are still true.
function reasonFor(period: Period): string {
  // First, because it is the CAUSE of the rest: a declared total the feed cannot
  // account for leaves every owner short, and "3 owners off" would send someone
  // hunting for a missing payment that was never going to be there.
  if (period.unaccountedCents > 0) return "More declared than seen";
  if (period.unknownCents > 0) return "Paid off the cap table";
  if (!period.balanced) return "Cap table ≠ 100%";
  const short = period.lines.filter((line) => !line.matched).length;
  return `${short} owner${short === 1 ? "" : "s"} off`;
}

// The quarter this payment's date settles, plus its neighbours — the only
// plausible corrections. A distribution is never four quarters late; offering every
// quarter since incorporation would bury the two that actually matter.
function neighbouringPeriods(derived: string, current: string): string[] {
  const [year, quarter] = derived.split("-Q").map(Number);
  const shift = (offset: number) => {
    const index = (year * 4 + (quarter - 1)) + offset;
    return `${Math.floor(index / 4)}-Q${(index % 4) + 1}`;
  };
  return [...new Set([shift(-1), derived, shift(1), current])].sort();
}

function CandidateTable({
  candidates,
  classifying,
  selected,
  onSelect,
  onFlag,
}: {
  candidates: readonly Candidate[];
  classifying: ReadonlySet<string>;
  selected: ReadonlySet<string>;
  onSelect: (next: ReadonlySet<string>) => void;
  onFlag: (txnIds: readonly string[]) => void;
}) {
  const [query, setQuery] = useState("");

  // Every cell the row renders, as one lowercased string. Built once per list
  // rather than per keystroke, and from the DISPLAYED text — the search has to
  // match what is on screen, so the date is searchable both as "Mar 1, 2025" and
  // as "2025-03", and the amount as "$1,000" and as the bare number.
  const haystacks = useMemo(
    () =>
      new Map(
        candidates.map((candidate) => [
          candidate.row.txn.id,
          [
            candidate.row.postingDate,
            day(candidate.row.postingDate),
            candidate.row.description,
            candidate.shareholder?.name ?? "—",
            candidate.row.classification ?? "unclassified",
            String(Math.abs(candidate.row.amount)),
            money(Math.abs(candidate.row.amount)),
          ]
            .join(" ")
            .toLowerCase(),
        ])
      ),
    [candidates]
  );

  // Every word has to match, so "alex salary" narrows instead of widening.
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  const visible =
    terms.length === 0
      ? candidates
      : candidates.filter((candidate) => {
          const hay = haystacks.get(candidate.row.txn.id) ?? "";
          return terms.every((term) => hay.includes(term));
        });

  // Selection spans the whole worklist; the checkboxes speak for what is on
  // screen. Select-all takes the rows you can see and leaves any hidden pick
  // alone — dropping those on a keystroke would silently undo real work — and
  // the bar below says so when the two disagree.
  const chosen = candidates.filter((candidate) => selected.has(candidate.row.txn.id));
  const visibleChosen = visible.filter((candidate) => selected.has(candidate.row.txn.id));
  const allVisibleChosen = visible.length > 0 && visibleChosen.length === visible.length;
  const chosenTotal = chosen.reduce((sum, candidate) => sum + Math.abs(candidate.row.amount), 0);
  const hiddenChosen = chosen.length - visibleChosen.length;

  const toggle = (txnId: string) => {
    const next = new Set(selected);
    if (next.has(txnId)) next.delete(txnId);
    else next.add(txnId);
    onSelect(next);
  };

  const toggleAll = () => {
    const next = new Set(selected);
    visible.forEach((candidate) => {
      if (allVisibleChosen) next.delete(candidate.row.txn.id);
      else next.add(candidate.row.txn.id);
    });
    onSelect(next);
  };

  return (
    <div className="bg-white rounded border overflow-hidden">
      <div className="flex items-center gap-3 border-b px-4 py-2">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter by anything on a row — payee, description, month, amount…"
          className="flex-1 rounded border border-gray-200 px-2 py-1 text-sm focus:border-violet-400 focus:outline-none"
        />
        <span className="text-xs text-gray-400 whitespace-nowrap">
          {visible.length === candidates.length
            ? `${candidates.length} payments`
            : `${visible.length} of ${candidates.length}`}
        </span>
        {query && (
          <button
            type="button"
            className="text-xs text-gray-500 hover:text-gray-700"
            onClick={() => setQuery("")}
          >
            Clear
          </button>
        )}
      </div>
      {chosen.length > 0 && (
        <div className="flex items-center gap-3 border-b bg-violet-50 px-4 py-2 text-sm">
          <span className="font-medium">
            {chosen.length} selected · {money(chosenTotal)}
          </span>
          {hiddenChosen > 0 && (
            <span className="text-xs text-violet-700">
              {hiddenChosen} of them hidden by the filter
            </span>
          )}
          <button
            type="button"
            className={buttonClass}
            onClick={() => onFlag(chosen.map((candidate) => candidate.row.txn.id))}
          >
            {chosen.length === 1 ? "It's a dividend" : "They're all dividends"}
          </button>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700"
            onClick={() => onSelect(new Set())}
          >
            Clear
          </button>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b bg-gray-50">
            <th className="px-4 py-2 font-medium">
              <input
                type="checkbox"
                checked={allVisibleChosen}
                // Partly-selected is a third state a checked/unchecked prop cannot
                // express; it has to be set on the node.
                ref={(node) => {
                  if (node) node.indeterminate = visibleChosen.length > 0 && !allVisibleChosen;
                }}
                disabled={visible.length === 0}
                onChange={toggleAll}
                aria-label="Select every payment shown"
              />
            </th>
            <th className="px-4 py-2 font-medium">Date</th>
            <th className="px-4 py-2 font-medium">Description</th>
            <th className="px-4 py-2 font-medium">Shareholder</th>
            <th className="px-4 py-2 font-medium">Currently</th>
            <th className="px-4 py-2 font-medium text-right">Amount</th>
            <th className="px-4 py-2" />
          </tr>
        </thead>
        <tbody>
          {visible.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-400">
                No payment on this list matches “{query}”.
              </td>
            </tr>
          )}
          {visible.map((candidate) => (
            <tr
              key={candidate.row.md5}
              aria-busy={classifying.has(candidate.row.txn.id)}
              className={`border-b last:border-0 transition-opacity ${
                classifying.has(candidate.row.txn.id)
                  ? "opacity-50"
                  : selected.has(candidate.row.txn.id)
                    ? "bg-violet-50/60"
                    : ""
              }`}
            >
              <td className="px-4 py-2">
                <input
                  type="checkbox"
                  checked={selected.has(candidate.row.txn.id)}
                  disabled={classifying.has(candidate.row.txn.id)}
                  onChange={() => toggle(candidate.row.txn.id)}
                  aria-label={`Select ${candidate.row.description}`}
                />
              </td>
              <td className="px-4 py-2 whitespace-nowrap">{day(candidate.row.postingDate)}</td>
              <td className="px-4 py-2 max-w-md truncate text-gray-600" title={candidate.row.description}>
                {candidate.row.description}
              </td>
              <td className="px-4 py-2">{candidate.shareholder?.name ?? "—"}</td>
              <td className="px-4 py-2">
                {candidate.row.classification ? (
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                    {candidate.row.classification}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">unclassified</span>
                )}
              </td>
              <td className="px-4 py-2 text-right tabular-nums">{money(Math.abs(candidate.row.amount))}</td>
              <td className="px-4 py-2 text-right">
                <button
                  type="button"
                  className={`${buttonClass} inline-flex items-center gap-1 disabled:cursor-default`}
                  disabled={classifying.has(candidate.row.txn.id)}
                  onClick={() => onFlag([candidate.row.txn.id])}
                >
                  {classifying.has(candidate.row.txn.id) && (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  )}
                  It's a dividend
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-500 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-gray-400 mt-0.5">{hint}</span>}
    </label>
  );
}

const inputClass =
  "w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-violet-400 focus:outline-none";
const buttonClass =
  "rounded bg-violet-600 px-3 py-1 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50";
